import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, BookOpenCheck, CheckCircle2, Sparkles } from "lucide-react";
import { CHECKOUT_URL, OLD_PRICE, PRICE } from "@/constants/checkout";
import { EASE } from "./Reveal";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const BookMockup = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -5]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-24, -6]), { stiffness: 120, damping: 16 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div
      data-testid="hero-book-mockup"
      className="relative"
      style={{ perspective: 1400 }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <motion.div
        initial={{ opacity: 0, y: 70 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.15, delay: 0.55, ease: EASE }}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative mx-auto w-[240px] sm:w-[300px] lg:w-[330px]"
      >
        <motion.div
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="relative"
        >
          <div className="relative aspect-[840/1190] overflow-hidden rounded-l-md rounded-r-2xl border border-linen bg-sand shadow-[0_50px_90px_-25px_rgba(26,38,29,0.5)]">
            <img
              src="/assets/capa-ebook.png"
              alt="Capa oficial do e-book Nutrição Zero ao Equilíbrio"
              data-testid="hero-book-cover"
              className="h-full w-full object-cover"
            />
          </div>
          <div className="absolute -right-2 bottom-[4px] top-[4px] w-2 rounded-r-sm bg-[repeating-linear-gradient(90deg,#f5f1e6_0px,#f5f1e6_1px,#ddd6c4_2px)]" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.15, duration: 0.6, ease: EASE }}
          className="absolute -left-8 top-8 -rotate-6 rounded-2xl border border-linen bg-white/95 px-4 py-2.5 shadow-lg backdrop-blur"
        >
          <p className="flex items-center gap-1.5 text-xs font-semibold text-forest">
            <BookOpenCheck size={14} className="text-terracotta" /> Exercício + checklist por capítulo
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.35, duration: 0.6, ease: EASE }}
          className="absolute -right-4 bottom-12 rotate-3 rounded-2xl border border-linen bg-white/95 px-4 py-2.5 shadow-lg backdrop-blur"
        >
          <p className="flex items-center gap-1.5 text-xs font-semibold text-forest">
            <Sparkles size={14} className="text-ochre" /> Leitura de uma tarde
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default function Hero() {
  return (
    <section
      id="topo"
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      <div className="pointer-events-none absolute -left-32 top-16 h-96 w-96 rounded-full bg-sage/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-[28rem] w-[28rem] rounded-full bg-ochre/15 blur-3xl" />
      <p className="text-outline pointer-events-none absolute -bottom-8 left-0 select-none whitespace-nowrap font-serif text-[9rem] font-bold italic leading-none opacity-60 lg:text-[13rem]">
        equilíbrio
      </p>

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 pb-28 pt-16 sm:px-8 lg:grid-cols-2 lg:gap-8 lg:pt-24">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-linen bg-mint px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-forest"
          >
            <Sparkles size={13} className="text-terracotta" /> E-book de nutrição descomplicada
          </motion.p>

          <h1 className="font-serif text-4xl font-bold leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>Aprenda a comer bem,</MaskedLine>
            <MaskedLine delay={0.32}>
              <span className="italic text-terracotta">sem complicar.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-md text-base leading-relaxed text-moss sm:text-lg"
          >
            O método direto em 11 capítulos para sair do “não sei o que comer” e montar
            pratos equilibrados toda semana — sem dieta maluca, sem culpa e sem começar do zero.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.72, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4"
          >
            <div>
              <p className="text-sm text-moss">
                De <span className="line-through">R$ {OLD_PRICE}</span> por apenas
              </p>
              <p className="font-serif text-4xl font-bold text-ink">
                R$ {PRICE}
                <span className="ml-2 align-middle text-xs font-sans font-semibold uppercase tracking-wider text-terracotta">
                  pagamento único
                </span>
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-7 flex flex-wrap items-center gap-4"
          >
            <motion.a
              href={CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-button"
              whileTap={{ scale: 0.97 }}
              className="cta-shine inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-base font-semibold text-cream shadow-[0_18px_40px_-12px_rgba(200,109,81,0.55)] transition-transform duration-300 hover:-translate-y-1"
            >
              Quero meu e-book agora <ArrowRight size={18} />
            </motion.a>
            <a
              href="#metodo"
              data-testid="hero-secondary-link"
              onClick={(e) => {
                e.preventDefault();
                const el = document.querySelector("#metodo");
                if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -108 });
              }}
              className="text-sm font-semibold text-forest underline decoration-terracotta/50 decoration-2 underline-offset-4 transition-colors hover:text-terracotta"
            >
              Conhecer o método
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs font-medium text-moss"
          >
            {["Acesso imediato", "Garantia de 7 dias", "Pix ou cartão"].map((t) => (
              <li key={t} className="flex items-center gap-1.5">
                {t === "Garantia de 7 dias" ? (
                  <img
                    src="/assets/selo-garantia.png"
                    alt="Selo compra garantida, 7 dias, 100% seguro"
                    data-testid="hero-guarantee-seal"
                    className="h-5 w-5 object-contain"
                  />
                ) : (
                  <CheckCircle2 size={14} className="text-sage" />
                )}
                {t}
              </li>
            ))}
          </motion.ul>
        </div>

        <BookMockup />
      </div>
    </section>
  );
}
